import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { User, WorkspaceRole } from '@/types/auth.types';
import { Permission, SubscriptionPermissions } from '@/types/permission';
import { SubscriptionType } from '@/types/shared';
import { CreditsContext } from '@/services/CreditsContext';
import {
  calculateCreditsPermissions,
  calculatePlanPermissions,
  calculateSeatsPermissions,
  calculateManagePaymentsPermissions,
  calculateModelBlockingPermissions,
} from './useSubscriptionPermissions.utils';

interface PermissionsContextType {
  creditsPermissions: Permission;
  planPermissions: Permission;
  seatsPermissions: Permission;
  managePaymentsPermissions: Permission;
  modelBlockingPermissions: Permission;
  isWorkspaceAdmin: boolean;
  isAllowedForPermission: (permission: keyof SubscriptionPermissions, action: keyof Permission) => boolean;
  isSubscriptionTypeIn: (allowedFor: SubscriptionType[]) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType>({
  creditsPermissions: { view: false, action: false },
  planPermissions: { view: false, action: false },
  seatsPermissions: { view: false, action: false },
  managePaymentsPermissions: { view: false, action: false },
  modelBlockingPermissions: { view: false, action: false },
  isWorkspaceAdmin: false,
  isAllowedForPermission: () => false,
  isSubscriptionTypeIn: () => false,
});

interface SubscriptionPermissionsProviderProps {
  user: User | null;
  children: ReactNode;
}

export const SubscriptionPermissionsProvider: React.FC<SubscriptionPermissionsProviderProps> = ({ user, children }) => {
  const { shouldShowCreditsToMembers } = useContext(CreditsContext);

  const [creditsPermissions, setCreditsPermissions] = useState<Permission>({ view: false, action: false });
  const [planPermissions, setPlanPermissions] = useState<Permission>({ view: false, action: false });
  const [seatsPermissions, setSeatsPermissions] = useState<Permission>({ view: false, action: false });
  const [modelBlockingPermissions, setModelBlockingPermissions] = useState<Permission>({
    view: false,
    action: false,
  });
  const [managePaymentsPermissions, setManagePaymentsPermissions] = useState<Permission>({
    view: false,
    action: false,
  });

  const initCreditsPermissions = useCallback(() => {
    if (!user?.activeWorkspace) {
      return;
    }

    const { role, subscription } = user.activeWorkspace;
    const subType = subscription?.type;

    const permissions = calculateCreditsPermissions(role, subType, shouldShowCreditsToMembers);
    setCreditsPermissions(permissions);
  }, [user?.activeWorkspace, shouldShowCreditsToMembers]);

  const initPlanPermissions = useCallback(() => {
    if (!user?.activeWorkspace) {
      return;
    }

    const { role, subscription } = user.activeWorkspace;
    const subType = subscription?.type;

    const permissions = calculatePlanPermissions(role, subType);
    setPlanPermissions(permissions);
  }, [user?.activeWorkspace]);

  const initSeatsPermissions = useCallback(() => {
    if (!user?.activeWorkspace) {
      return;
    }

    const { role, subscription } = user.activeWorkspace;
    const subType = subscription?.type;

    const permissions = calculateSeatsPermissions(role, subType);
    setSeatsPermissions(permissions);
  }, [user?.activeWorkspace]);

  const initManagePaymentsPermissions = useCallback(() => {
    if (!user?.activeWorkspace) {
      return;
    }

    const { role, subscription } = user.activeWorkspace;
    const subType = subscription?.type;

    const permissions = calculateManagePaymentsPermissions(role, subType);
    setManagePaymentsPermissions(permissions);
  }, [user?.activeWorkspace]);

  const initModelBlockingPermissions = useCallback(() => {
    if (!user?.activeWorkspace) {
      return;
    }

    const subType = user.activeWorkspace.subscription?.type;
    const isModelBlockingEnabled = user.activeWorkspace.isModelBlockingEnabled;

    const permissions = calculateModelBlockingPermissions(subType, isModelBlockingEnabled);
    setModelBlockingPermissions(permissions);
  }, [user?.activeWorkspace]);

  useEffect(() => {
    initCreditsPermissions();
    initPlanPermissions();
    initSeatsPermissions();
    initManagePaymentsPermissions();
    initModelBlockingPermissions();
  }, [
    initCreditsPermissions,
    initPlanPermissions,
    initSeatsPermissions,
    initManagePaymentsPermissions,
    user,
    initModelBlockingPermissions,
  ]);

  /**
   * This function checks if a permission is allowed for the current subscription type
   * @param permission - the permission to check
   * @param action - the action to check
   * @returns true if the permission is allowed for the current subscription type
   */
  const isAllowedForPermission = (permission: keyof SubscriptionPermissions, action: keyof Permission): boolean => {
    switch (permission) {
      case 'credits':
        return creditsPermissions[action];
      case 'plan':
        return planPermissions[action];
      case 'seats':
        return seatsPermissions[action];
      case 'managePayments':
        return managePaymentsPermissions[action];
      case 'modelBlocking':
        return modelBlockingPermissions[action];
      default:
        return false;
    }
  };

  /**
   * This function checks if an arbitrary action is allowed for the current subscription type
   * Use this for any arbitrary action that is not covered by built in permissions
   * @param allowedFor - list of subscription types that are allowed to access the action
   */
  const isSubscriptionTypeIn = (allowedFor: SubscriptionType[]): boolean => {
    if (!user?.activeWorkspace?.subscription?.type) {
      return false;
    }
    return allowedFor.includes(user?.activeWorkspace?.subscription?.type);
  };

  const isWorkspaceAdmin = user?.activeWorkspace?.role === WorkspaceRole.Admin;

  const value = {
    creditsPermissions,
    planPermissions,
    seatsPermissions,
    managePaymentsPermissions,
    modelBlockingPermissions,
    isWorkspaceAdmin,
    isAllowedForPermission,
    isSubscriptionTypeIn,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const useSubscriptionPermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('useSubscriptionPermissions must be used within a SubscriptionPermissionsProvider');
  }
  return context;
};
